import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand } from "lucide-react";
import { Reveal, EASE } from "./motion";
import { PhotoSlot } from "./PhotoSlot";
import { GALLERY_ITEMS } from "../lib/constants";

const SPANS = [
  "sm:col-span-2 sm:row-span-2 aspect-[4/3] sm:aspect-auto",
  "aspect-[4/3]",
  "aspect-[4/3]",
  "aspect-[4/3]",
  "aspect-[4/3]",
  "sm:col-span-2 aspect-[16/9] sm:aspect-auto",
  "aspect-[4/3]",
  "aspect-[4/3]",
];

export const GalleryGrid = ({ items = GALLERY_ITEMS, limit }) => {
  const [selected, setSelected] = useState(null);
  const list = limit ? items.slice(0, limit) : items;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:auto-rows-[220px]" data-testid="clinic-gallery">
        {list.map((item, i) => (
          <Reveal key={item.label} delay={(i % 3) * 0.08} className={SPANS[i % SPANS.length]}>
            <button
              className="group relative w-full h-full rounded-2xl overflow-hidden text-left focus-visible:ring-2"
              onClick={() => setSelected(item)}
              data-testid={`gallery-item-${i}`}
              aria-label={`View ${item.label} photograph`}
            >
              <PhotoSlot
                label={item.label}
                note={item.note || ""}
                src={item.src}
                alt={item.alt}
                dark={i % 3 === 0}
                className="w-full h-full rounded-2xl transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand className="w-4 h-4" aria-hidden="true" />
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink-deep/90 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={() => setSelected(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`${selected.label} enlarged view`}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <PhotoSlot
                label={selected.label}
                note={selected.note || ""}
                src={selected.src}
                alt={selected.alt}
                fit={selected.src ? "contain" : "cover"}
                dark
                className={selected.src ? "w-full rounded-3xl" : "w-full aspect-[4/3] rounded-3xl"}
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="text-white font-bold">{selected.label}</p>
                <button
                  onClick={() => setSelected(null)}
                  className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                  aria-label="Close enlarged view"
                  data-testid="lightbox-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
